import { Badge, BorderRadius, Button, Color, Divider, Icon, Input, TabButtonsGroup } from '@billy/design-system'
import { ChangeEvent, ReactElement, useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { FILTER_TYPE_ORDER } from '../../constants/reviewEntriesFilterConfig'
import { ReviewEntriesFilterType } from '../../enums/reviewEntriesFilterType'
import { ReviewEntriesViewFilter } from '../../enums/reviewEntriesViewFilter'
import { ReviewEntriesFilterTypeMenu } from '../ReviewEntriesFiltersBar/ReviewEntriesFilterTypeMenu'
import { ReviewEntriesColumnVisibilityMenu } from './elements/ReviewEntriesColumnVisibilityMenu'
import * as Styled from './styles'

interface ReviewEntriesTableToolbarProps {
  addedFilterTypes: ReviewEntriesFilterType[]
  hiddenColumnIds: string[]
  searchQuery: string
  toValidateCount: number
  viewFilter: ReviewEntriesViewFilter
  onAddFilter: (type: ReviewEntriesFilterType) => void
  onHiddenColumnIdsChange: (hiddenColumnIds: string[]) => void
  onSearchChange: (value: string) => void
  onSearchClear: () => void
  onViewFilterChange: (value: ReviewEntriesViewFilter) => void
}

export const ReviewEntriesTableToolbar = ({
  addedFilterTypes,
  hiddenColumnIds,
  onAddFilter,
  onHiddenColumnIdsChange,
  onSearchChange,
  onSearchClear,
  onViewFilterChange,
  searchQuery,
  toValidateCount,
  viewFilter,
}: ReviewEntriesTableToolbarProps): ReactElement => {
  const { t } = useTranslation()

  const availableFilterTypes = useMemo(
    () => FILTER_TYPE_ORDER.filter((type) => !addedFilterTypes.includes(type)),
    [addedFilterTypes],
  )

  const handleChangeDebounced = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      onSearchChange(event.target.value)
    },
    [onSearchChange],
  )

  const handleToValidateClick = useCallback(() => {
    onViewFilterChange(ReviewEntriesViewFilter.ToValidate)
  }, [onViewFilterChange])

  const handleAllClick = useCallback(() => {
    onViewFilterChange(ReviewEntriesViewFilter.All)
  }, [onViewFilterChange])

  return (
    <Styled.Toolbar>
      <Styled.SearchWrapper>
        <Input
          allowClear
          bordered={false}
          isRounded
          onChangeDebounced={handleChangeDebounced}
          onClear={onSearchClear}
          placeholder={t('review_entries.toolbar.search.placeholder')}
          prefix={<Icon icon="search" />}
          size="sm"
          value={searchQuery}
        />
        <Divider direction="vertical" dividerColor={Color.Grey200} />
        <Styled.FiltersButtonWrapper>
          <ReviewEntriesFilterTypeMenu
            availableTypes={availableFilterTypes}
            onSelect={onAddFilter}
            trigger={
              <Button
                borderRadius={`0 ${BorderRadius.RFull} ${BorderRadius.RFull} 0`}
                disabled={availableFilterTypes.length === 0}
                fullWidth
                icon="listFilter"
                size="sm"
                skipLeftRounding
                variant="text"
              >
                {t('review_entries.toolbar.filters')}
              </Button>
            }
          />
        </Styled.FiltersButtonWrapper>
      </Styled.SearchWrapper>
      <Styled.ColumnVisibilityWrapper>
        <ReviewEntriesColumnVisibilityMenu
          hiddenColumnIds={hiddenColumnIds}
          onHiddenColumnIdsChange={onHiddenColumnIdsChange}
        />
      </Styled.ColumnVisibilityWrapper>
      <TabButtonsGroup>
        <TabButtonsGroup.Button
          onClick={handleToValidateClick}
          selected={viewFilter === ReviewEntriesViewFilter.ToValidate}
          suffix={<Badge color="accent">{toValidateCount}</Badge>}
        >
          {t('review_entries.status.to_validate')}
        </TabButtonsGroup.Button>
        <TabButtonsGroup.Button onClick={handleAllClick} selected={viewFilter === ReviewEntriesViewFilter.All}>
          {t('review_entries.toolbar.all')}
        </TabButtonsGroup.Button>
      </TabButtonsGroup>
    </Styled.Toolbar>
  )
}
